import type {
  StaffImportsApi,
  StaffImportsCallOptions,
  StaffImportsCommandOptions,
} from "@lafayette/api-client";
import type {
  StaffImportAliasQuery,
  StaffImportApproveBody,
  StaffImportBulkReviewBody,
  StaffImportCancelBody,
  StaffImportCreateDraftBody,
  StaffImportJobListQuery,
  StaffImportPreviewBody,
  StaffImportRecordDecisionBody,
  StaffImportRetryBody,
  StaffImportReviewBody,
  StaffImportReviewRowListQuery,
  StaffImportRowListQuery,
  StaffImportSaveMappingBody,
  StaffImportSubmitBody,
  StaffImportUploadTarget,
  StaffImportUploadTargetBody,
} from "@lafayette/schemas";
import type { DataProvider } from "@refinedev/core";

// The Refine data provider of the importer. It talks to the Lafayette API through the typed
// `StaffImportsApi`, never to Supabase tables. It is registered under the name `imports`; the resource
// `catalog-imports` (see `AdminApplication.tsx`) uses it.
//   getList  R1: the jobs, keyset pages (`meta.cursor`, `meta.status`, `meta.limit`)
//   getOne   R2: one job with its latest mapping revision
//   custom   every command and the other reads, named in `meta.command`
// The API pages with an opaque cursor and sends no total. Update and delete are not offered: every
// change is a domain command (save a mapping, submit, cancel).

export const IMPORTS_DATA_PROVIDER = "imports";
export const importResources = { jobs: "catalog-imports" } as const;

type CommandContext = Readonly<{ jobId?: string | undefined }>;
type Executor = (
  api: StaffImportsApi,
  context: CommandContext,
  payload: never,
  options: StaffImportsCommandOptions,
) => Promise<unknown>;

const jobIdOf = (context: CommandContext): string => {
  if (context.jobId === undefined) throw new Error("This import command needs a job id.");
  return context.jobId;
};

// One entry per call. A new method in the contract's client has to be named here before the UI can run it.
const executors = {
  createUploadTarget: (api, _c, payload: StaffImportUploadTargetBody, o) =>
    api.createUploadTarget(payload, o),
  uploadFile: (
    api,
    _c,
    payload: Readonly<{ target: StaffImportUploadTarget; body: Blob | ArrayBuffer | Uint8Array }>,
    o,
  ) => api.uploadFile(payload.target, payload.body, o),
  previewFile: (api, _c, payload: StaffImportPreviewBody, o) => api.previewFile(payload, o),
  createDraft: (api, _c, payload: StaffImportCreateDraftBody, o) => api.createDraft(payload, o),
  saveMapping: (api, c, payload: StaffImportSaveMappingBody, o) =>
    api.saveMapping(jobIdOf(c), payload, o),
  submitMapping: (api, c, payload: StaffImportSubmitBody, o) =>
    api.submitMapping(jobIdOf(c), payload, o),
  cancelJob: (api, c, payload: StaffImportCancelBody, o) => api.cancelJob(jobIdOf(c), payload, o),
  listRows: (api, c, payload: StaffImportRowListQuery, o) => api.listRows(jobIdOf(c), payload, o),
  listAliases: (api, _c, payload: StaffImportAliasQuery, o) => api.listAliases(payload, o),
  // CHT-708: review, approval, processing and retry.
  listReviewRows: (api, c, payload: StaffImportReviewRowListQuery, o) =>
    api.listReviewRows(jobIdOf(c), payload, o),
  getReviewRow: (api, c, payload: Readonly<{ rowId: string }>, o) =>
    api.getReviewRow(jobIdOf(c), payload.rowId, o),
  getProgress: (api, c, _payload: undefined, o) => api.getProgress(jobIdOf(c), o),
  recordDecision: (
    api,
    c,
    payload: Readonly<{ rowId: string; body: StaffImportRecordDecisionBody }>,
    o,
  ) => api.recordDecision(jobIdOf(c), payload.rowId, payload.body, o),
  reviewRows: (api, c, payload: StaffImportReviewBody, o) => api.reviewRows(jobIdOf(c), payload, o),
  bulkReview: (api, c, payload: StaffImportBulkReviewBody, o) =>
    api.bulkReview(jobIdOf(c), payload, o),
  approveJob: (api, c, payload: StaffImportApproveBody, o) =>
    api.approveJob(jobIdOf(c), payload, o),
  retryJob: (api, c, payload: StaffImportRetryBody, o) => api.retryJob(jobIdOf(c), payload, o),
} as const satisfies Record<string, Executor>;

export type ImportCommandName = keyof typeof executors;
export const importCommandNames = Object.keys(executors) as readonly ImportCommandName[];

/** What a command passes in `meta` (Refine's `custom` has no typed slot for it). */
export type ImportCommandMeta = Readonly<{
  command: ImportCommandName;
  jobId?: string;
  idempotencyKey?: string;
  signal?: AbortSignal;
}>;

/** What a list read passes in `meta`. */
type ImportListMeta = Readonly<{
  status?: StaffImportJobListQuery["status"];
  cursor?: string | null;
  limit?: number;
  signal?: AbortSignal;
}>;

const callOptions = (meta: ImportListMeta | undefined): StaffImportsCallOptions =>
  meta?.signal === undefined ? {} : { signal: meta.signal };

function unsupported(operation: string): never {
  throw new Error(
    `The import data provider does not support "${operation}": use a domain command.`,
  );
}

export function createImportsDataProvider(api: StaffImportsApi): DataProvider {
  const provider = {
    getApiUrl: () => "",

    getList: (async ({ resource, meta }: { resource: string; meta?: unknown }) => {
      if (resource !== importResources.jobs) return unsupported(`getList ${resource}`);
      const listMeta = (meta ?? {}) as ImportListMeta;
      const query: StaffImportJobListQuery = {
        ...(listMeta.status === undefined ? {} : { status: listMeta.status }),
        ...(listMeta.cursor === undefined || listMeta.cursor === null
          ? {}
          : { cursor: listMeta.cursor }),
        ...(listMeta.limit === undefined ? {} : { limit: listMeta.limit }),
      };
      const page = await api.listJobs(query, callOptions(listMeta));
      return { data: page.items, total: page.items.length, nextCursor: page.nextCursor };
    }) as DataProvider["getList"],

    getOne: (async ({ resource, id, meta }: { resource: string; id: unknown; meta?: unknown }) => {
      if (resource !== importResources.jobs) return unsupported(`getOne ${resource}`);
      const detail = await api.getJob(String(id), callOptions(meta as ImportListMeta | undefined));
      return { data: detail };
    }) as DataProvider["getOne"],

    create: (async () => unsupported("create")) as DataProvider["create"],
    update: (async () => unsupported("update")) as DataProvider["update"],
    deleteOne: (async () => unsupported("deleteOne")) as DataProvider["deleteOne"],

    custom: (async ({ meta, payload }: { meta?: unknown; payload?: unknown }) => {
      const command = meta as ImportCommandMeta | undefined;
      if (command === undefined || !(command.command in executors)) {
        return unsupported("custom without a known command");
      }
      const options: StaffImportsCommandOptions = {
        ...(command.idempotencyKey === undefined ? {} : { idempotencyKey: command.idempotencyKey }),
        ...(command.signal === undefined ? {} : { signal: command.signal }),
      };
      const run = executors[command.command] as unknown as (
        api: StaffImportsApi,
        context: CommandContext,
        payload: unknown,
        options: StaffImportsCommandOptions,
      ) => Promise<unknown>;
      return { data: await run(api, { jobId: command.jobId }, payload, options) };
    }) as DataProvider["custom"],
  };
  // The generic method signatures of Refine's DataProvider do not fit a typed client; each method above is cast once.
  return provider as unknown as DataProvider;
}
